import { useState, useEffect, useRef } from "react";
import * as faceapi from "@vladmandic/face-api";
import axios from "api/axios";
import useCurrentUser from "hooks/useCurrentUser";

/* ── Icons ──────────────────────────────────────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {Array.isArray(d)
      ? d.map((p, i) => <path key={i} d={p} />)
      : <path d={d} />}
  </svg>
);

const IconUser = () => (
  <Icon
    d={[
      "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2",
      "M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
    ]}
  />
);

const IconCamera = () => (
  <Icon
    d={[
      "M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z",
      "M12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
    ]}
  />
);

const IconCheck = () => (
  <Icon d="M20 6 9 17 4 12" stroke={2.5} />
);

const IconX = () => (
  <Icon
    d="M18 6 6 18M6 6l12 12"
    size={18}
    stroke={2}
  />
);

const IconWave = () => (
  <Icon
    d={[
      "M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0z",
      "M6 11v5a6 6 0 0 0 12 0v-5",
    ]}
  />
);

const IconScan = () => (
  <Icon
    d={[
      "M14.5 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z",
      "M21 21l-4.35-4.35",
      "M3 11a8 8 0 0 1 15.31-2.35",
      "M12 3v3",
    ]}
  />
);

const IconFingerprint = () => (
  <Icon
    d={[
      "M12 11a1 1 0 0 1 1 1v3a1 1 0 0 1-2 0v-3a1 1 0 0 1 1-1z",
      "M7.05 11a5 5 0 1 1 9.9 0",
      "M4.22 11a8 8 0 1 1 15.56 0",
      "M1.39 11a10.7 10.7 0 0 1 21.22 0",
    ]}
  />
);

/* ── Premium Scoped CSS ──────────────────────────────── */
const KIOSK_CSS = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }

  @keyframes glow-pulse {
    0%, 100% {
      box-shadow: 0 0 30px rgba(99,102,241,0.3);
    }

    50% {
      box-shadow:
        0 0 60px rgba(99,102,241,0.5),
        0 0 120px rgba(139,92,246,0.2);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% center;
    }

    100% {
      background-position: 200% center;
    }
  }

  @keyframes slide-up {
    from {
      opacity: 0;
      transform: translateY(30px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes scale-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .kiosk-root {
    min-height: 100vh;
    background: #0a0c16;
    background-image:
      radial-gradient(
        ellipse at 50% 0%,
        rgba(99,102,241,0.08) 0%,
        transparent 60%
      ),
      radial-gradient(
        ellipse at 80% 100%,
        rgba(139,92,246,0.05) 0%,
        transparent 50%
      );
    color: #e2e4f0;
    font-family: 'DM Sans', system-ui, -apple-system, sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 24px;
    position: relative;
    overflow: hidden;
  }

  .kiosk-root::before,
  .kiosk-root::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.12;
    pointer-events: none;
  }

  .kiosk-root::before {
    width: 400px;
    height: 400px;
    background: #6366f1;
    top: -100px;
    right: -100px;
  }

  .kiosk-root::after {
    width: 300px;
    height: 300px;
    background: #8b5cf6;
    bottom: -80px;
    left: -80px;
  }

  .kiosk-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 32px;
    position: relative;
    z-index: 1;
  }

  .kiosk-header-icon {
    width: 56px;
    height: 56px;
    border-radius: 18px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 8px 32px rgba(99,102,241,0.4);
    animation: float 4s ease-in-out infinite;
  }

  .kiosk-header-content h2 {
    font-size: 26px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.02em;
    margin: 0;
    line-height: 1.2;
  }

  .kiosk-header-content span {
    font-size: 13px;
    color: rgba(255,255,255,0.45);
    font-weight: 500;
  }

  .kiosk-camera-wrapper {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 600px;
    margin-bottom: 28px;
    animation: slide-up 0.6s ease;
  }

  .kiosk-camera-box {
    position: relative;
    width: 100%;
    aspect-ratio: 4/3;
    background: #000;
    border-radius: 24px;
    overflow: hidden;
    border: 2px solid rgba(255,255,255,0.08);
    box-shadow:
      0 20px 60px rgba(0,0,0,0.6),
      0 0 0 1px rgba(255,255,255,0.05) inset;
    transition: all 0.4s ease;
  }

  .kiosk-camera-box video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .kiosk-camera-box.flash-success {
    border-color: rgba(46,213,115,0.6);
    box-shadow:
      0 20px 60px rgba(0,0,0,0.6),
      0 0 40px rgba(46,213,115,0.3);
  }

  .kiosk-camera-box.flash-error {
    border-color: rgba(255,71,87,0.6);
    box-shadow:
      0 20px 60px rgba(0,0,0,0.6),
      0 0 40px rgba(255,71,87,0.3);
  }

  .kiosk-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 2;
  }

  .kiosk-oval {
    width: 240px;
    height: 300px;
    border-radius: 50% / 50%;
    border: 2.5px solid rgba(99,102,241,0.5);
    box-shadow:
      0 0 30px rgba(99,102,241,0.15),
      0 0 0 9999px rgba(0,0,0,0.2);
    animation: glow-pulse 3s ease-in-out infinite;
  }

  .kiosk-welcome-overlay {
    position: absolute;
    inset: 0;
    background: rgba(10, 12, 22, 0.93);
    backdrop-filter: blur(12px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
    animation: scale-in 0.4s ease;
  }

  .kiosk-welcome-icon {
    width: 80px;
    height: 80px;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    animation: float 3s ease-in-out infinite;
  }

  .kiosk-welcome-icon.clock-in {
    background: linear-gradient(135deg, #2ed573, #1dd1a1);
    box-shadow: 0 12px 32px rgba(46,213,115,0.5);
  }

  .kiosk-welcome-icon.clock-out {
    background: linear-gradient(135deg, #ffa800, #ff8c00);
    box-shadow: 0 12px 32px rgba(255,168,0,0.5);
  }

  .kiosk-welcome-greeting {
    font-size: 15px;
    color: rgba(255,255,255,0.5);
    margin-bottom: 4px;
    font-weight: 500;
  }

  .kiosk-welcome-name {
    font-size: 32px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.03em;
    background: linear-gradient(
      135deg,
      #fff 0%,
      #c4b5fd 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .kiosk-welcome-action {
    font-size: 15px;
    font-weight: 600;
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .kiosk-welcome-action.clock-in {
    color: #2ed573;
  }

  .kiosk-welcome-action.clock-out {
    color: #ffa800;
  }

  .kiosk-welcome-time {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    margin-top: 16px;
    padding: 8px 16px;
    background: rgba(255,255,255,0.04);
    border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.06);
  }

  .kiosk-status {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: rgba(255,255,255,0.5);
    margin-bottom: 24px;
    padding: 10px 20px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 30px;
    position: relative;
    z-index: 1;
  }

  .kiosk-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2ed573;
    animation: kiosk-pulse 1.5s ease infinite;
    box-shadow: 0 0 10px rgba(46,213,115,0.5);
  }

  @keyframes kiosk-pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }

    50% {
      opacity: 0.6;
      transform: scale(0.85);
    }
  }

  .kiosk-footer {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(255,255,255,0.2);
    position: relative;
    z-index: 1;
    padding-top: 20px;
  }

  .kiosk-footer svg {
    opacity: 0.5;
  }
`;

export default function AttendanceKiosk() {
 

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  /*
   * IMPORTANT:
   *
   * FaceMatcher is kept in STATE rather than only in a ref.
   *
   * A change to ref.current does not cause React to re-render.
   * Therefore the old implementation could have:
   *
   *   cameraReady = true
   *   faceMatcherRef.current = null
   *
   * The detection effect would return and never start again when
   * faceMatcherRef.current was later populated.
   */
  const [faceMatcher, setFaceMatcher] = useState(null);

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [error, setError] = useState("");

  const [flash, setFlash] = useState(null);
  const [branchDescriptors, setBranchDescriptors] = useState([]);

  const [welcome, setWelcome] = useState(null);

  /*
   * Prevent two attendance requests from being sent at the
   * same time if detection runs again while an API request
   * is still being processed.
   */
  const processingRef = useRef(false);

  /*
   * Used to make sure setTimeout callbacks created for the
   * welcome screen are cleaned up when the component unmounts.
   */
  const welcomeTimeoutRef = useRef(null);
  const flashTimeoutRef = useRef(null);

  // ─────────────────────────────────────────────────────
  // Inject CSS
  // ─────────────────────────────────────────────────────
  useEffect(() => {
    const styleId = "kiosk-css";

    if (!document.getElementById(styleId)) {
      const el = document.createElement("style");

      el.id = styleId;
      el.textContent = KIOSK_CSS;

      document.head.appendChild(el);
    }

    return () => {
      const existing = document.getElementById(styleId);

      if (existing) {
        existing.remove();
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────
  // Load face-api models
  // ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const loadModels = async () => {
      try {
        console.log(
          "[KIOSK] Loading face-api models..."
        );

        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(
            "/models"
          ),

          faceapi.nets.faceLandmark68Net.loadFromUri(
            "/models"
          ),

          faceapi.nets.faceRecognitionNet.loadFromUri(
            "/models"
          ),
        ]);

        if (cancelled) {
          return;
        }

        console.log(
          "[KIOSK] Face-api models loaded successfully"
        );

        setModelsLoaded(true);
      } catch (err) {
        console.error(
          "[KIOSK] Face-api model loading failed:",
          err
        );

        if (!cancelled) {
          setError(
            "Could not load face-recognition models. Check the browser console."
          );
        }
      }
    };

    loadModels();

    return () => {
      cancelled = true;
    };
  }, []);

  // ─────────────────────────────────────────────────────
  // Load branch face descriptors
  // ─────────────────────────────────────────────────────
  useEffect(() => {
  if (!modelsLoaded) {
    return;
  }

  let cancelled = false;

  const loadFaceDescriptors = async () => {
    try {
      console.log("[KIOSK] Loading all face descriptors...");

      const response = await axios.get(
        "/api/rh/attendance/face-descriptors"
      );

      if (cancelled) {
        return;
      }

      const descriptors = Array.isArray(response.data)
        ? response.data
        : [];

      console.log(
        "[KIOSK] Received",
        descriptors.length,
        "face descriptors"
      );

      setBranchDescriptors(descriptors);
    } catch (err) {
      console.error(
        "[KIOSK] Failed to load face descriptors:",
        err
      );

      if (!cancelled) {
        setError(
          "Failed to load face data."
        );
      }
    }
  };

  loadFaceDescriptors();

  return () => {
    cancelled = true;
  };
}, [modelsLoaded]);

  // ─────────────────────────────────────────────────────
  // Build FaceMatcher
  // ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!modelsLoaded) {
      return;
    }

    if (!branchDescriptors.length) {
      console.warn(
        "[KIOSK] No face descriptors available."
      );

      setFaceMatcher(null);

      return;
    }

    try {
      console.log(
        "[KIOSK] Building FaceMatcher from",
        branchDescriptors.length,
        "descriptors..."
      );

      const labeledDescriptors = branchDescriptors
        .map((descriptorData) => {
          try {
            const parsed =
              typeof descriptorData.descriptor === "string"
                ? JSON.parse(
                    descriptorData.descriptor
                  )
                : descriptorData.descriptor;

            if (!parsed) {
              console.error(
                "[KIOSK] Empty descriptor:",
                descriptorData
              );

              return null;
            }

            /*
             * Face-api face descriptors must contain
             * exactly 128 floating-point values.
             */
            const values = Array.isArray(parsed)
              ? parsed
              : Array.from(parsed);

            if (values.length !== 128) {
              console.error(
                "[KIOSK] Invalid descriptor length:",
                values.length,
                "expected 128",
                descriptorData
              );

              return null;
            }

            const float32 =
              new Float32Array(values);

            return new faceapi.LabeledFaceDescriptors(
              `${descriptorData.userId}::${descriptorData.firstName} ${descriptorData.lastName}`,
              [float32]
            );
          } catch (err) {
            console.error(
              "[KIOSK] Could not parse face descriptor:",
              descriptorData,
              err
            );

            return null;
          }
        })
        .filter(Boolean);

      if (!labeledDescriptors.length) {
        console.error(
          "[KIOSK] No valid face descriptors found."
        );

        setFaceMatcher(null);

        setError(
          "No valid enrolled faces found for this branch."
        );

        return;
      }

      /*
       * 0.5 is the matching distance threshold.
       *
       * Lower = stricter.
       * Higher = more permissive.
       */
      const matcher = new faceapi.FaceMatcher(
        labeledDescriptors,
        0.5
      );

      console.log(
        "[KIOSK] FaceMatcher created successfully.",
        "Faces:",
        labeledDescriptors.length
      );

      setFaceMatcher(matcher);
    } catch (err) {
      console.error(
        "[KIOSK] Failed to create FaceMatcher:",
        err
      );

      setFaceMatcher(null);

      setError(
        "Failed to initialize face recognition."
      );
    }
  }, [modelsLoaded, branchDescriptors]);

  // ─────────────────────────────────────────────────────
  // Start camera
  // ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!modelsLoaded) {
      return;
    }

    let cancelled = false;

    const startCamera = async () => {
      try {
        console.log(
          "[KIOSK] Requesting camera..."
        );

        if (
          !navigator.mediaDevices ||
          !navigator.mediaDevices.getUserMedia
        ) {
          throw new Error(
            "Camera API is not supported by this browser."
          );
        }

        const stream =
          await navigator.mediaDevices.getUserMedia({
            video: {
              width: {
                ideal: 640,
              },

              height: {
                ideal: 480,
              },

              facingMode: "user",
            },

            audio: false,
          });

        if (cancelled) {
          stream
            .getTracks()
            .forEach((track) => track.stop());

          return;
        }

        streamRef.current = stream;

        const videoTrack =
          stream.getVideoTracks()[0];

        console.log(
          "[KIOSK] Camera stream started:",
          videoTrack?.getSettings()
        );

        if (!videoRef.current) {
          console.warn(
            "[KIOSK] Video element not available."
          );

          stream
            .getTracks()
            .forEach((track) => track.stop());

          return;
        }

        videoRef.current.srcObject = stream;

        videoRef.current.onloadedmetadata =
          async () => {
            if (cancelled) {
              return;
            }

            console.log(
              "[KIOSK] Video metadata loaded:",
              videoRef.current.videoWidth,
              "x",
              videoRef.current.videoHeight
            );

            try {
              await videoRef.current.play();
            } catch (playError) {
              console.warn(
                "[KIOSK] Video play warning:",
                playError
              );
            }

            if (!cancelled) {
              setCameraReady(true);

              console.log(
                "[KIOSK] Camera ready."
              );
            }
          };
      } catch (err) {
        console.error(
          "[KIOSK] Camera error:",
          err
        );

        if (!cancelled) {
          setError(
            `Camera access denied or unavailable: ${
              err?.message || "Unknown error"
            }`
          );
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;

      setCameraReady(false);

      if (streamRef.current) {
        streamRef.current
          .getTracks()
          .forEach((track) => track.stop());

        streamRef.current = null;
      }
    };
  }, [modelsLoaded]);

  // ─────────────────────────────────────────────────────
  // Face detection + recognition loop
  // ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!cameraReady) {
      return;
    }

    if (!faceMatcher) {
      console.log(
        "[KIOSK] Camera ready, waiting for FaceMatcher..."
      );

      return;
    }

    if (!videoRef.current) {
      console.warn(
        "[KIOSK] Camera ready but video element is missing."
      );

      return;
    }

    let cancelled = false;
    let timeoutId = null;

    console.log(
      "[KIOSK] Starting face detection loop..."
    );

    const scheduleNext = (delay) => {
      if (!cancelled) {
        timeoutId = setTimeout(
          detectAndMatch,
          delay
        );
      }
    };

    const detectAndMatch = async () => {
      if (cancelled) {
        return;
      }

      const video = videoRef.current;

      if (!video) {
        return;
      }

      /*
       * Don't start another detection while an attendance
       * request is currently being processed.
       */
      if (processingRef.current) {
        scheduleNext(1000);
        return;
      }

      try {
        /*
         * Make sure the video actually contains frames.
         */
        if (
          video.readyState <
          HTMLMediaElement.HAVE_CURRENT_DATA
        ) {
          console.log(
            "[KIOSK] Video is not ready yet."
          );

          scheduleNext(1000);

          return;
        }

        if (
          video.videoWidth === 0 ||
          video.videoHeight === 0
        ) {
          console.log(
            "[KIOSK] Video has zero dimensions."
          );

          scheduleNext(1000);

          return;
        }

        console.log(
          "[KIOSK] Detecting face..."
        );

        /*
         * Detect:
         * 1. face
         * 2. landmarks
         * 3. 128-value face descriptor
         */
        const detection = await faceapi
          .detectSingleFace(
            video,
            new faceapi.TinyFaceDetectorOptions({
              inputSize: 416,
              scoreThreshold: 0.5,
            })
          )
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (cancelled) {
          return;
        }

        if (!detection) {
          console.log(
            "[KIOSK] No face detected."
          );

          scheduleNext(1000);

          return;
        }

        console.log(
          "[KIOSK] Face detected."
        );

        /*
         * Compare the detected face against the
         * enrolled branch faces.
         */
        const match =
          faceMatcher.findBestMatch(
            detection.descriptor
          );

        console.log(
          "[KIOSK] Match:",
          match.label,
          "distance:",
          match.distance
        );

        if (match.label === "unknown") {
          console.log(
            "[KIOSK] Face detected but not recognized."
          );

          scheduleNext(1500);

          return;
        }

        /*
         * Label format:
         *
         * UUID::FirstName LastName
         */
        const separatorIndex =
          match.label.indexOf("::");

        if (separatorIndex === -1) {
          console.error(
            "[KIOSK] Invalid face matcher label:",
            match.label
          );

          scheduleNext(1500);

          return;
        }

        const userId =
          match.label.substring(
            0,
            separatorIndex
          );

        const displayName =
          match.label.substring(
            separatorIndex + 2
          );

        if (!userId || !displayName) {
          console.error(
            "[KIOSK] Invalid matched user:"
          );

          scheduleNext(1500);

          return;
        }

        /*
         * Lock processing before making API requests.
         */
        processingRef.current = true;

        console.log(
          "[KIOSK] Recognized employee:",
          userId,
          displayName
        );

        /*
         * Get current status first so the UI can say
         * Clocked In or Clocked Out.
         */
        const statusResponse =
          await axios.get(
            `/api/rh/attendance/status/${userId}`
          );

        if (cancelled) {
          processingRef.current = false;
          return;
        }

        const clockedIn =
          Boolean(
            statusResponse.data?.clockedIn
          );

        console.log(
          "[KIOSK] Employee currently clocked in:",
          clockedIn
        );

        /*
         * Backend decides whether this is clock-in
         * or clock-out.
         */
        await axios.post(
          "/api/rh/attendance/face-clock",
          {
            employeeId: userId,
          }
        );

        if (cancelled) {
          processingRef.current = false;
          return;
        }

        const action = clockedIn
          ? "Clocked Out"
          : "Clocked In";

        console.log(
          "[KIOSK] Attendance successfully recorded:",
          action
        );

        setWelcome({
          name: displayName,
          action,
        });

        setFlash("success");

        /*
         * Clear old timers before creating new ones.
         */
        if (welcomeTimeoutRef.current) {
          clearTimeout(
            welcomeTimeoutRef.current
          );
        }

        if (flashTimeoutRef.current) {
          clearTimeout(
            flashTimeoutRef.current
          );
        }

        welcomeTimeoutRef.current =
          setTimeout(() => {
            if (!cancelled) {
              setWelcome(null);
            }
          }, 4000);

        flashTimeoutRef.current =
          setTimeout(() => {
            if (!cancelled) {
              setFlash(null);
            }
          }, 4000);

        processingRef.current = false;

        /*
         * Wait 6 seconds before scanning again.
         * This prevents the same person from immediately
         * clocking out after clocking in.
         */
        scheduleNext(6000);
      } catch (err) {
        console.error(
          "[KIOSK] Detection/matching error:",
          err
        );

        processingRef.current = false;

        if (!cancelled) {
          setFlash("error");

          if (flashTimeoutRef.current) {
            clearTimeout(
              flashTimeoutRef.current
            );
          }

          flashTimeoutRef.current =
            setTimeout(() => {
              if (!cancelled) {
                setFlash(null);
              }
            }, 2000);
        }

        scheduleNext(2000);
      }
    };

    /*
     * Start detection immediately.
     */
    detectAndMatch();

    return () => {
      cancelled = true;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      console.log(
        "[KIOSK] Face detection loop stopped."
      );
    };
  }, [
    cameraReady,
    faceMatcher,
    
  ]);

  // ─────────────────────────────────────────────────────
  // Cleanup timers
  // ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (welcomeTimeoutRef.current) {
        clearTimeout(
          welcomeTimeoutRef.current
        );
      }

      if (flashTimeoutRef.current) {
        clearTimeout(
          flashTimeoutRef.current
        );
      }

      if (streamRef.current) {
        streamRef.current
          .getTracks()
          .forEach((track) => track.stop());

        streamRef.current = null;
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────
  // Date / time
  // ─────────────────────────────────────────────────────
  const now = new Date();

  const timeString =
    now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const dateString =
    now.toLocaleDateString([], {
      weekday: "long",
      month: "long",
      day: "numeric",
    });

  // ─────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────
  return (
    <div className="kiosk-root">

      {/* Header */}
      <div className="kiosk-header">

        <div className="kiosk-header-icon">
          <IconFingerprint size={28} />
        </div>

        <div className="kiosk-header-content">

          <h2>
            Attendance Kiosk
          </h2>

          <span>
            Face Recognition · Touchless Clock-In
          </span>

        </div>

      </div>

      {/* Camera */}
      <div className="kiosk-camera-wrapper">

        <div
          className={`kiosk-camera-box${
            flash === "success"
              ? " flash-success"
              : flash === "error"
                ? " flash-error"
                : ""
          }`}
        >

          {error ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "rgba(255,255,255,0.3)",
                fontSize: 14,
                padding: 24,
                textAlign: "center",
                flexDirection: "column",
                gap: 12,
              }}
            >

              <IconX size={32} />

              <span>
                {error}
              </span>

            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
              />

              {!welcome && (
                <div className="kiosk-overlay">
                  <div className="kiosk-oval" />
                </div>
              )}

              {welcome && (
                <div className="kiosk-welcome-overlay">

                  <div
                    className={`kiosk-welcome-icon ${
                      welcome.action === "Clocked In"
                        ? "clock-in"
                        : "clock-out"
                    }`}
                  >

                    {welcome.action ===
                    "Clocked In" ? (
                      <IconCheck size={36} />
                    ) : (
                      <IconWave size={36} />
                    )}

                  </div>

                  <div className="kiosk-welcome-greeting">

                    {welcome.action ===
                    "Clocked In"
                      ? "Welcome,"
                      : "Goodbye,"}

                  </div>

                  <div className="kiosk-welcome-name">

                    {welcome.name}

                  </div>

                  <div
                    className={`kiosk-welcome-action ${
                      welcome.action === "Clocked In"
                        ? "clock-in"
                        : "clock-out"
                    }`}
                  >

                    {welcome.action ===
                    "Clocked In"
                      ? "Have a great day! ☀️"
                      : "See you tomorrow! 👋"}

                  </div>

                  <div className="kiosk-welcome-time">

                    {dateString} · {timeString}

                  </div>

                </div>
              )}

            </>
          )}

        </div>

      </div>

      {/* Status */}
      <div className="kiosk-status">

        <span className="kiosk-status-dot" />

        {!modelsLoaded
          ? "Loading face-recognition models..."

          : !cameraReady
            ? "Initializing camera..."

            : !faceMatcher
              ? branchDescriptors.length === 0
                ? "Loading face data..."
                : "Initializing face recognition..."

              : welcome
                ? "Next scan available in a moment..."

                : "Look directly at the camera to mark your attendance"}

      </div>

      {/* Footer */}
      <div className="kiosk-footer">

        <IconScan size={14} />

        StockPOS Attendance System

      </div>

    </div>
  );
}